import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import styled from 'styled-components';

import getItemCount from './getItemCount';
import getItemSize from './getItemSize';
import SquareGrid from './squareGrid';

// TODO 230 Remove CSS transitions on everything
const Container = styled.div`
  overflow-y: scroll;
  height: ${props => (props.isVisible ? '100%' : '0')};
  transition: none;
`;

// TODO Resize bug:
// - Looks like bug in library https://github.com/danbovey/react-infinite-scroller/issues/169
// - react-infinite-scroll-component has the same bug
// - Canva also got this bug.
// - This can be solved by:
//   - Ensure the content covers the viewport
//   - Listen to viewport resizing and ensure page cover.
//   - Threshold can be small (big enough to fetch next photos).
//   - Add photos in batch.
// - Probably for the first version preloading screen height into scroll area should be enough to not worry about resizes.
//   Better use screen height + 1, for case of fullscreen usage.
// TODO Create something like AsyncSquareGrid with props for easier async fetching of records?
// - New prop fetchItems(itemCount) => Promise.resolve([])
//   Must return the required item count, otherwise scrolling is ended.
//   Or maybe return it explicitly: Promise.resolve({items: [], hasMore: false})
// - Props no more needed: hasMore, isLoading, loadMore and maybe items (how to replace the loaded items?).
// - Async flow handling like `setIsLoading` should be implemented there.
const InfiniteSquareGrid = ({
  columnCount,
  columnGap,
  hasMore,
  isVisible,
  isLoading,
  items,
  loadMore,
  loader,
  renderItem,
  rowGap,
  threshold,
}) => {
  const [containerElement, setContainerElement] = useState();
  const [initialLoad, setInitialLoad] = useState(true);

  const ref = useCallback(element => {
    if (element != null) {
      setContainerElement(element);
    }
  }, []);

  const visibleItems = useMemo(() => {
    // If there are no more available items, display everything.
    if (!hasMore) {
      return items;
    }
    // Otherwise display only the whole rows, don't show the last incomplete row.
    const { length } = items;
    const visibleLength = length - (length % columnCount);
    return items.slice(0, visibleLength);
  }, [columnCount, hasMore, items]);

  /**
   * Has more value need to be converted to infinite scroller, where it's meaning is more like:
   * "Enable / disable the scroll watchers and the whole functionality".
   * To support async request, hasMore needs to be set to false, or the scroller crashes with:
   * Maximum update depth exceeded.
   * For those situations loader should be visible even when hasMore is false.
   *
   * Sources:
   * - https://github.com/danbovey/react-infinite-scroller/issues/143#issuecomment-421973425
   */
  const hasMoreProxy = useMemo(() => hasMore && isVisible && !isLoading, [
    hasMore,
    isVisible,
    isLoading,
  ]);

  const loadMoreProxy = page => {
    setInitialLoad(false);

    // Compute the size of one item in the grid.
    const itemSize = getItemSize(columnCount, columnGap, containerElement.offsetWidth);
    // Compute the count of items needed to cover the whole container.
    const initialItemCount = getItemCount(
      columnCount,
      containerElement.clientHeight,
      itemSize,
      rowGap,
    );
    // Compute the count of items that are added while scrolling, one row for each page.
    // One more row is loaded, so the user can start to scroll after the initial items.
    const pageItemCount = (page + 1) * columnCount;
    return loadMore({ itemCount: initialItemCount + pageItemCount });
  };

  return (
    <Container isVisible={isVisible} ref={ref}>
      {containerElement && (
        <InfiniteScroll
          hasMore={hasMoreProxy}
          pageStart={0}
          loadMore={loadMoreProxy}
          threshold={threshold}
          useWindow={false}
          initialLoad={initialLoad}
        >
          <SquareGrid
            columnCount={columnCount}
            columnGap={columnGap}
            items={visibleItems}
            renderItem={renderItem}
            rowGap={rowGap}
          />
          {isLoading && <div style={{ marginTop: visibleItems.length ? rowGap : 0 }}>{loader}</div>}
        </InfiniteScroll>
      )}
    </Container>
  );
};

InfiniteSquareGrid.defaultProps = {
  columnCount: 1,
  columnGap: 0,
  hasMore: false,
  isLoading: false,
  isVisible: true,
  loader: undefined,
  rowGap: 0,
  threshold: 250,
};

InfiniteSquareGrid.propTypes = {
  columnCount: PropTypes.number,
  columnGap: PropTypes.number,
  hasMore: PropTypes.bool,
  isLoading: PropTypes.bool,
  isVisible: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadMore: PropTypes.func.isRequired,
  loader: PropTypes.object,
  renderItem: PropTypes.func.isRequired,
  rowGap: PropTypes.number,
  threshold: PropTypes.number,
};

export default InfiniteSquareGrid;
